import React from "react";
import { Stack, Typography, Box } from "@mui/material";
import { Warning as WarningIcon } from "@mui/icons-material";
import { ActionModal } from "@/components";
import { useDeleteDutyColumnMutation } from "@/services/product-catalogue/duty-columns-api";
import { DutyColumn } from "@/services/product-catalogue/duty-columns-api";
import { tokens } from "@/tokens";

interface DeleteDutyColumnDialogProps {
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
  column: DutyColumn | null;
}

const DeleteDutyColumnDialog: React.FC<DeleteDutyColumnDialogProps> = ({
  open,
  onClose,
  onDeleted,
  column,
}) => {
  const [deleteDutyColumn, { isLoading }] = useDeleteDutyColumnMutation();

  const handleDelete = async () => {
    if (!column) return;

    try {
      await deleteDutyColumn(column._id).unwrap();
      onDeleted();
    } catch (error) {
      console.error("Failed to delete duty column:", error);
    }
  };

  if (!column) return null;

  return (
    <ActionModal
      show={open}
      setShow={() => onClose()}
      title="Delete Duty Column"
      subTitle="This action cannot be undone"
      buttonText="Delete Column"
      buttonAction={handleDelete}
      isConfirmButtonLoading={isLoading}
      maxWidth="sm"
      modalStyles={{
        "& .MuiPaper-root": {
          borderRadius: "16px",
        },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Stack spacing={3}>
          <Box sx={{ 
            p: 3, 
            borderRadius: tokens.radius.lg,
            backgroundColor: tokens.status.dangerBg,
            border: '1px solid #fecaca',
          }}>
            <Typography variant="body1" color={tokens.text.body} sx={{ mb: 2 }}>
              Are you sure you want to delete this duty column? This will permanently remove:
            </Typography>
            
            <Box sx={{ 
              p: 2, 
              backgroundColor: tokens.bg.surface,
              borderRadius: tokens.radius.md,
              border: `1px solid ${tokens.border.default}`,
            }}>
              <Stack spacing={1}>
                <Typography variant="body2" color={tokens.text.secondary}>
                  <strong>Column Name:</strong> {column.name}
                </Typography>
                <Typography variant="body2" color={tokens.text.secondary}>
                  <strong>Description:</strong> {column.description || 'N/A'}
                </Typography>
                <Typography variant="body2" color={tokens.text.secondary}>
                  <strong>Number of Rules:</strong> {column.rules?.length || 0}
                </Typography>
                <Typography variant="body2" color={tokens.text.secondary}>
                  <strong>Status:</strong> {column.isActive ? 'Active' : 'Inactive'}
                </Typography>
              </Stack>
            </Box>
          </Box>

          <Box sx={{ 
            p: 3, 
            borderRadius: tokens.radius.lg,
            backgroundColor: tokens.status.warningBg,
            border: '1px solid #fed7aa',
          }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <WarningIcon sx={{ color: tokens.status.warning, fontSize: 20 }} />
              <Typography variant="body2" color={tokens.chip.amber.text} fontWeight={tokens.fontWeight.semibold}>
                This action is permanent and cannot be undone. All associated rules and tariff calculations will be lost.
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Box>
    </ActionModal>
  );
};

export default DeleteDutyColumnDialog; 